import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Plus, Trash2 } from "lucide-react";
import {
  commissionsApi,
  type CommissionAllocationDto,
  type CommissionPositionDto,
  type RevenuePoolDto,
  type UpsertCommissionAllocationRequest,
} from "@/api/commissions";
import { ApiError } from "@/api/client";
import { commissionKeys } from "@/hooks/commissions/commissionKeys";
import { Badge, Button, FormField, Input, Table, TBody, TD, TH, THead, TR } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { UnallocatedPoolNotice } from "@/components/commissions/UnallocatedPoolNotice";
import { AllocationDeleteConfirmModal } from "@/components/commissions/AllocationDeleteConfirmModal";

export function AllocationAdminPage() {
  const allocations = useQuery({
    queryKey: commissionKeys.allocations(),
    queryFn: () => commissionsApi.listAllocations(),
  });
  const [editing, setEditing] = useState<CommissionAllocationDto | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <div className="flex flex-col gap-space-5">
      <div className="flex items-end justify-between gap-space-3">
        <div>
          <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-2">
            Commissions · Admin
          </div>
          <h1 className="font-display font-extrabold text-h2 uppercase tracking-wider text-onyx m-0">
            Allocation table
          </h1>
          <p className="text-body text-gray-mid mt-space-2 max-w-reading">
            The standing position×pool rates. When servicing income is recognized, the auto-suggest
            engine reads these to draft a claim for each position at percent × the pool&apos;s gross.
          </p>
        </div>
        <Button variant="primary" onClick={() => setCreating(true)}>
          <Plus size={14} strokeWidth={2} aria-hidden /> New allocation
        </Button>
      </div>

      <UnallocatedPoolNotice />

      {allocations.isLoading ? (
        <div className="font-display text-label uppercase tracking-label text-gray-mid">Loading…</div>
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>Position</TH>
              <TH>Pool</TH>
              <TH className="text-right">Rate</TH>
              <TH className="text-right">Actions</TH>
            </TR>
          </THead>
          <TBody>
            {allocations.data?.map((a) => (
              <TR key={a.id}>
                <TD>
                  <span className="font-display font-bold text-onyx">{a.roleCode}</span>
                  <span className="text-caption text-gray-mid ml-space-2">{a.roleTitle}</span>
                </TD>
                <TD>
                  {a.poolCode} · {a.poolName}
                  {a.poolIsRetired && (
                    <Badge status="archived" className="ml-space-2">
                      Retired
                    </Badge>
                  )}
                </TD>
                <TD className="text-right tabular-nums">{a.percent.toFixed(2)}%</TD>
                <TD className="text-right">
                  <div className="flex justify-end gap-space-2">
                    <Button variant="secondary" size="sm" onClick={() => setEditing(a)}>
                      Edit
                    </Button>
                    <DeleteAllocationButton allocation={a} />
                  </div>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}

      {creating && <AllocationEditor allocation={null} onClose={() => setCreating(false)} />}
      {editing && <AllocationEditor allocation={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function DeleteAllocationButton({ allocation }: { allocation: CommissionAllocationDto }) {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const remove = useMutation({
    mutationFn: () => commissionsApi.deleteAllocation(allocation.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: commissionKeys.rates() });
      // The pool summary is a SIBLING of rates() under commissionKeys.all, not
      // a descendant, so the prefix match above does not reach it — and this
      // page renders UnallocatedPoolNotice, which reads exactly that payload.
      // Removing a pool's last allocation flips its hasAllocations, so without
      // this the banner stays wrong for the 5-minute staleTime with no
      // recovery path (refetchOnWindowFocus is off).
      qc.invalidateQueries({ queryKey: commissionKeys.poolSummary() });
      setConfirming(false);
    },
    onError: (e) => setError(e instanceof ApiError ? (e.errors[0] ?? e.message) : String(e)),
  });
  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => setConfirming(true)}
        aria-label="Delete allocation"
      >
        <Trash2 size={14} strokeWidth={1.5} aria-hidden />
      </Button>
      {confirming && (
        <AllocationDeleteConfirmModal
          allocation={allocation}
          deleting={remove.isPending}
          error={error}
          onCancel={() => {
            setError(null);
            setConfirming(false);
          }}
          onConfirm={() => {
            setError(null);
            remove.mutate();
          }}
        />
      )}
    </>
  );
}

interface EditorProps {
  allocation: CommissionAllocationDto | null;
  onClose: () => void;
}

const SELECT_CLASS =
  "w-full px-space-3 py-space-2 border border-border-light rounded-md font-body text-small bg-white-pure focus:border-gold focus:outline-none";

function AllocationEditor({ allocation, onClose }: EditorProps) {
  const qc = useQueryClient();
  const pools = useQuery({
    queryKey: commissionKeys.poolList("all"),
    queryFn: () => commissionsApi.listPools(true),
  });
  const positions = useQuery({
    queryKey: commissionKeys.allocationPositions(),
    queryFn: () => commissionsApi.listAllocationPositions(),
  });
  const [roleCode, setRoleCode] = useState(allocation?.roleCode ?? "");
  const [revenuePoolId, setRevenuePoolId] = useState(allocation?.revenuePoolId ?? "");
  const [percent, setPercent] = useState<string>(allocation ? String(allocation.percent) : "");
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const payload: UpsertCommissionAllocationRequest = {
        roleCode: roleCode.trim(),
        revenuePoolId,
        percent: Number(percent),
      };
      return allocation
        ? commissionsApi.updateAllocation(allocation.id, payload)
        : commissionsApi.createAllocation(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: commissionKeys.rates() });
      // Sibling of rates(), not a descendant — see DeleteAllocationButton.
      // Adding a pool's FIRST allocation flips hasAllocations the other way.
      qc.invalidateQueries({ queryKey: commissionKeys.poolSummary() });
      onClose();
    },
    onError: (e) => setError(e instanceof ApiError ? (e.errors[0] ?? e.message) : String(e)),
  });

  // A stored role code outside the catalog must still render on the (disabled)
  // edit field: a <select> whose value matches no <option> shows BLANK, which
  // would silently hide the row's own position. This field was free text
  // historically and creation still does not validate against the catalog, so
  // such a value is legitimately reachable.
  //
  // The option is kept at ALL times so the select is never blank, but the
  // "not in the catalog" CLAIM is only made once the catalog is actually in
  // hand. `positions.data ?? []` alone collapses pending, error and
  // loaded-empty into one empty list, and asserting absence against a list we
  // simply do not have yet labels perfectly valid codes as uncatalogued on
  // every cold open.
  const positionOptions = useMemo<CommissionPositionDto[]>(() => {
    const list = positions.data ?? [];
    if (roleCode && !list.some((p) => p.code === roleCode)) {
      // Blank title => the option renders as the bare code (see below).
      return [
        ...list,
        { code: roleCode, title: positions.isSuccess ? "not in the position catalog" : "" },
      ];
    }
    return list;
  }, [positions.data, positions.isSuccess, roleCode]);

  const parsed = Number(percent);
  const canSave =
    roleCode.trim().length > 0 &&
    revenuePoolId.length > 0 &&
    percent.trim().length > 0 &&
    !Number.isNaN(parsed) &&
    parsed >= 0 &&
    parsed <= 100;

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={allocation ? `Edit ${allocation.roleCode} / ${allocation.poolCode}` : "New allocation"}
      size="md"
      footer={
        <>
          <div className="flex-1" />
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!canSave}
            loading={save.isPending}
            onClick={() => {
              setError(null);
              save.mutate();
            }}
          >
            Save
          </Button>
        </>
      }
    >
      {error && (
        <div role="alert" className="flex items-start gap-space-2 p-space-3 border border-status-error rounded-md bg-status-error/5 text-status-error text-small mb-space-3">
          <AlertTriangle size={16} strokeWidth={1.5} aria-hidden />
          <span>{error}</span>
        </div>
      )}
      <div className="flex flex-col gap-space-3">
        <FormField
          label="Position (role code)"
          required
          helperText="The commission position this rate pays."
        >
          <select
            aria-label="Position (role code)"
            value={roleCode}
            onChange={(e) => setRoleCode(e.target.value)}
            disabled={!!allocation}
            className={SELECT_CLASS}
          >
            <option value="">Select a position…</option>
            {positionOptions.map((p) => (
              <option key={p.code} value={p.code}>
                {p.title ? `${p.code} · ${p.title}` : p.code}
              </option>
            ))}
          </select>
          {positions.isError && !allocation && (
            // Only worth saying on CREATE: the position field is disabled on
            // edit, and the stored value still renders regardless. Before this
            // was a dropdown it was free text, so a catalog that fails to load
            // could not previously block anything.
            <div className="text-caption text-status-error mt-space-1" role="alert">
              The position list could not be loaded, so there is nothing to pick from. Reload and
              try again.
            </div>
          )}
        </FormField>
        <FormField label="Revenue pool" required>
          <select
            aria-label="Revenue pool"
            value={revenuePoolId}
            onChange={(e) => setRevenuePoolId(e.target.value)}
            disabled={!!allocation}
            className={SELECT_CLASS}
          >
            <option value="">Select a pool…</option>
            {pools.data?.map((p: RevenuePoolDto) => (
              <option key={p.id} value={p.id}>
                {p.code} · {p.name}
                {p.isRetired ? " (retired)" : ""}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Rate %" required helperText="Percent of the pool's recognized revenue (0–100).">
          <Input
            type="number"
            step="0.01"
            min={0}
            max={100}
            value={percent}
            onChange={(e) => setPercent(e.target.value)}
          />
        </FormField>
      </div>
    </Modal>
  );
}
